import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { useGroupParentSupervisionEditable } from '../../hooks';
import { FHButton, FHSignatureDialog, FProgressBar } from '../../components/base';
import PageContainer from '../../components/layout/PageContainer';
import Controls from '../../components/layout/supervision-notes/group/Controls';
import Families from '../../components/layout/supervision-notes/group/Families';
import FamiliesResponse from '../../components/layout/supervision-notes/group/FamiliesResponse';
import GroupTrainingTopics from '../../components/layout/supervision-notes/group/GroupTrainingTopics';
import {
  clientActions,
  groupParentSupervisionActions,
  providerAssignmentActions,
} from '../../store';
import { history } from '../../helpers';
import { FORTA_BLACK, FORTA_GRAY4 } from '../../utils/colors';
import { getUtcOffset } from '../../utils/common';
import { SupervisionNoteStatus, Themes } from '../../constants';
import { GROUP_SN_FIELD_MIN_LENGTH } from '../../components/layout/supervision-notes/constants';
import PSNRedirect from './PSNRedirect';
import {
  checkAllTopicMapError,
  checkInvalidFamilyAttendance,
} from '../../components/layout/supervision-notes/group/utils';

import type { RootState } from '../../store';

function GroupSupervisionNote() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { loading: groupSNLoading, current: groupParentSN } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );
  const { id: groupSNId } = useParams();
  const isEditable = useGroupParentSupervisionEditable() || groupSNId === 'new';

  const [openSignatureDialog, setOpenSignatureDialog] = useState<boolean>(false);
  const [validate, setValidate] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      dispatch(providerAssignmentActions.clearAssignment());
    };
  }, []);

  useEffect(() => {
    if (!groupSNId || groupSNId === 'new') {
      dispatch(groupParentSupervisionActions.clearState());
      setValidate(false);
      return;
    }

    const fetchSupervisionNote = async () => {
      const { error, payload } = await dispatch(
        groupParentSupervisionActions.getGroupSupervision({
          groupSNId,
          getAccessTokenSilently,
        }),
      );
      if (error) {
        history.navigate('/dashboard');
      }
      if (!payload?.date_start || payload?.status === SupervisionNoteStatus.Completed) return;
      dispatch(
        clientActions.getClientsFamily({ getAccessTokenSilently, dateActive: payload.date_start }),
      );
      dispatch(groupParentSupervisionActions.getGroupTrainingTopics({ getAccessTokenSilently }));
    };

    fetchSupervisionNote();
    return () => {
      dispatch(groupParentSupervisionActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSNId]);

  const validateForm = () => {
    if (!groupParentSN) return;
    setValidate(true);

    const familyError = groupParentSN?.family_attendance?.length < 2;
    const topicError = groupParentSN?.training_topics?.length < 1;
    const responseError = groupParentSN?.family_attendance?.some(
      (item) => !item.response || item.response?.length < GROUP_SN_FIELD_MIN_LENGTH,
    );
    const containsInvalidFamilyAttendance = checkInvalidFamilyAttendance(
      groupParentSN?.duration ?? 0,
      groupParentSN?.date_start ?? '',
      groupParentSN?.family_attendance ?? [],
    );
    const allTopicError = groupParentSN?.family_attendance?.some((a) =>
      checkAllTopicMapError(a, groupParentSN?.training_topics ?? [], groupParentSN?.duration ?? 0),
    );
    const noTopicError = groupParentSN?.family_attendance?.some(
      (attendance) => (attendance.topics ?? []).length === 0,
    );
    if (familyError) {
      toast.error('At least two families must be added to conclude the note.');
      return;
    }
    if (containsInvalidFamilyAttendance) {
      toast.error('Family attendance contains invalid date/time. Please correct to proceed.');
      return;
    }
    if (topicError) {
      toast.error('At least one training topic must be added to conclude the note.');
      return;
    }
    if (noTopicError) {
      toast.error(
        'All families must have at least one training topic attended to conclude the note.',
      );
      return;
    }
    if (allTopicError) {
      toast.error(
        "Some families are marked as attending all topics but aren't eligible. Please correct to continue.",
      );
      return;
    }
    if (responseError) {
      toast.error('Please comeplete all family responses sections to conclude the note.');
      return;
    }
    setOpenSignatureDialog(true);
  };

  const handleSignatureAndSubmit = async (dataURL: string) => {
    if (!groupSNId || !dataURL) return;
    const data = {
      signature: dataURL,
      date_signed: moment(new Date()).toISOString(),
      utc_offset_date_signed: getUtcOffset(moment(new Date())),
    };

    const response = await dispatch(
      groupParentSupervisionActions.stopGroupSupervision({
        groupSNId,
        getAccessTokenSilently,
        data,
      }),
    );
    setOpenSignatureDialog(false);
    if (!response.error) {
      history.navigate('/dashboard');
    }
  };

  return (
    <PageContainer dataTestId="groupSupervisionNotePageContainer">
      {groupSNLoading && <FProgressBar />}
      <Controls isEditable={isEditable} />
      {(groupParentSN?.family_attendance?.length ?? 0) < 2 ? <PSNRedirect /> : null}
      <Box sx={{ opacity: groupParentSN ? 1 : 0.5 }}>
        <Box>
          <Families isEditable={isEditable} validate={validate} />
        </Box>
        <Box>
          <GroupTrainingTopics isEditable={isEditable} validate={validate} />
        </Box>
        <Box>
          <FamiliesResponse isEditable={isEditable} validate={validate} />
        </Box>
      </Box>

      {isEditable && groupParentSN && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <FHButton
            onClick={validateForm}
            disabled={groupSNLoading}
            data-testid="concludeGroupTrainingNoteButton"
          >
            Conclude
          </FHButton>
        </Box>
      )}
      <FHSignatureDialog
        headerText="Signature"
        cancelText="Clear Signature"
        open={openSignatureDialog}
        signing={false}
        onClose={() => {
          setOpenSignatureDialog(false);
        }}
        onSign={(dataURL: string) => {
          handleSignatureAndSubmit(dataURL);
        }}
        contentRenderer={() => (
          <Box fontSize={12} sx={{ color: FORTA_GRAY4 }}>
            <Typography
              lineHeight={'21px'}
              variant="body1"
              align="left"
              color={FORTA_BLACK}
              fontSize={12}
            >
              By signing and submitting these notes, plan and/or records (each a “record”), I hereby
              attest that (i) the entry for the date of service listed in the record accurately and
              completely reflects the services rendered by the named clinician herein; (ii) I have
              thoroughly reviewed the records; and (iii) I have discussed all applicable findings,
              outcomes, issues or discrepancies with the client and/or client&apos;s legal guardian,
              as appropriate.
            </Typography>
            <Typography
              mt={4}
              lineHeight={'21px'}
              variant="body1"
              align="left"
              color={FORTA_BLACK}
              fontSize={12}
            >
              I understand that any falsification, omission, or concealment of facts may subject me
              to administrative, civil, or criminal liability as well as termination of my
              employment with the company.
            </Typography>
          </Box>
        )}
        colorTheme={Themes.Primary}
      />
    </PageContainer>
  );
}

export default GroupSupervisionNote;
